import { useCallback, useEffect, useRef, useState } from 'react';
import { MediaClock } from '../../frame-sequence-player';

/**
 * Video-style buffering gate for frame (SCAN) playback: the clock stalls (the
 * seeker holds) while any visible camera lacks the frame for the current
 * playhead. One mechanism covers the initial gate, seek re-buffer and mid-play
 * underrun.
 *
 * A camera joins the stalled set when its current frame isn't decoded and
 * leaves when it is; isBuffering tracks whether that set is non-empty. Dedup
 * by set membership so per-frame rAF pings don't thrash React state — and so
 * a camera that's already stalled can't re-arm the gate after the fallback
 * timer skips it.
 *
 * When `enabled` is false (non-frames products) the gate is inert: reports are
 * ignored and isBuffering stays true — the caller must gate on another source.
 */
export function useFramesBufferingGate(
  clock: MediaClock | null,
  enabled: boolean
) {
  const [isBuffering, setBuffering] = useState(true);
  // Bumped on resetGate so the valve effect re-arms even when isBuffering
  // didn't change across the reset (refs alone can't re-trigger it).
  const [resetCount, setResetCount] = useState(0);
  const stalledCamerasRef = useRef<Set<string>>(new Set());
  // Set once the initial gate clears (all cameras ready at least once). After
  // that, the 6s valve no longer fires — a mid-play underrun holds honestly.
  const playbackStartedRef = useRef(false);

  const reportFramesReady = useCallback(
    (cameraId: string, ready: boolean) => {
      if (!enabled) return;
      const stalled = stalledCamerasRef.current;
      const was = stalled.has(cameraId);
      if (ready === !was) return; // no membership change
      if (ready) stalled.delete(cameraId);
      else stalled.add(cameraId);
      if (stalled.size === 0) playbackStartedRef.current = true;
      const buffering = stalled.size > 0;
      // Stop the clock SYNCHRONOUSLY the instant a camera underruns. MediaClock
      // is wall-clock based, so waiting for the isBuffering state -> effect
      // round trip to pause it (which lags under heavy decode load) lets the
      // seeker run ahead of the frozen frames — and the buffer then jumps
      // forward and cancels the in-flight frames it skipped past. Resuming is
      // left to the owner of the playback loop (gated on !isSeekerPaused), so
      // this path can only ever hold, never over-play.
      if (buffering) clock?.pause();
      setBuffering(buffering);
    },
    [clock, enabled]
  );

  // Re-arm the initial gate (call on a timeline reset): a jump to the reset
  // target needs fresh footage, and without this a slow/broken frame there
  // could hold the seeker unrecoverably (the valve is disabled once playback
  // has started).
  const resetGate = useCallback(() => {
    stalledCamerasRef.current.clear();
    playbackStartedRef.current = false;
    setResetCount((c) => c + 1);
  }, []);

  // Initial-gate safety valve only: if the FIRST frame never decodes, force-
  // release after 6s so startup can't hang on missing footage. Once playback
  // has started (playbackStartedRef), a mid-play underrun — slow 3G frame,
  // expired URL — deliberately holds the seeker rather than playing ahead of
  // the footage; that honest stall is the whole point of the buffering gate.
  useEffect(() => {
    if (!enabled || !isBuffering || playbackStartedRef.current) {
      return;
    }
    const gateTimer = window.setTimeout(() => setBuffering(false), 6000);
    return () => window.clearTimeout(gateTimer);
  }, [enabled, isBuffering, resetCount]);

  return { isBuffering, reportFramesReady, resetGate };
}
